import { ArrowRightOutlined } from "@ant-design/icons";
import { Flex, Space, Steps, Tag, Typography } from "antd";
import { WO_STATUS_TRANSITIONS } from "../constants/woStatusTransitions";

const STATUS_LABELS: Record<string, string> = {
  NEW: "New",
  IN_PROGRESS: "In Progress",
  STANDBY: "Standby",
  DELAYED: "Delayed",
  IN_REVIEW: "In Review",
  DONE: "Done",
};

const STATUS_COLORS: Record<string, string> = {
  NEW: "blue",
  IN_PROGRESS: "orange",
  STANDBY: "red",
  DELAYED: "volcano",
  IN_REVIEW: "purple",
  DONE: "green",
};

const LINEAR_STEPS = ["NEW", "IN_PROGRESS", "IN_REVIEW", "DONE"] as const;

type WOStatusIndicatorProps = {
  currentStatus: string;
  role: "management" | "field";
  statusBeforeDelay?: string | null;
};

// Neither Standby nor Delayed is a step of its own: both pause the work order where it
// already stood, so the stepper keeps that position and turns red, with a tag naming the
// pause. A delayed work order reports the status it held before the delay, so it can sit
// on New rather than always on In Progress the way a standby does.
const pausedStepIndex = (
  currentStatus: string,
  statusBeforeDelay: string | null | undefined
) => {
  const pausedAt = currentStatus === "DELAYED" ? (statusBeforeDelay ?? "") : "";
  const index = LINEAR_STEPS.indexOf(pausedAt as (typeof LINEAR_STEPS)[number]);
  return index === -1 ? 1 : index;
};

export const WOStatusIndicator = ({
  currentStatus,
  role,
  statusBeforeDelay,
}: WOStatusIndicatorProps) => {
  if (role === "management") {
    const isPaused = currentStatus === "STANDBY" || currentStatus === "DELAYED";
    const currentIndex = isPaused
      ? pausedStepIndex(currentStatus, statusBeforeDelay)
      : LINEAR_STEPS.indexOf(currentStatus as (typeof LINEAR_STEPS)[number]);
    const stepStatus = isPaused
      ? "error"
      : currentStatus === "DONE"
        ? "finish"
        : "process";

    return (
      <Flex vertical gap={4}>
        <Steps
          size="small"
          current={currentIndex}
          status={stepStatus}
          items={LINEAR_STEPS.map((s) => ({ title: STATUS_LABELS[s] }))}
        />
        {isPaused && <Tag color="warning">{STATUS_LABELS[currentStatus]}</Tag>}
      </Flex>
    );
  }

  const fieldActions = WO_STATUS_TRANSITIONS[currentStatus]?.field ?? [];
  const nextLabel = fieldActions[0]?.label ?? null;

  return (
    <Space>
      <Tag color={STATUS_COLORS[currentStatus]}>{STATUS_LABELS[currentStatus]}</Tag>
      {nextLabel && (
        <>
          <ArrowRightOutlined />
          <Typography.Text type="secondary">{nextLabel}</Typography.Text>
        </>
      )}
    </Space>
  );
};
